import { For, Match, Show, Switch, createMemo } from "solid-js";
import { Dynamic } from "solid-js/web";
import { parseMarkdown, type Block, type Inline } from "../lib/markdown.ts";
import styles from "./Markdown.module.scss";

export type MarkdownProps = {
  /** The Markdown source, as the analyst wrote it. */
  source: string;
};

/**
 * A dossier, rendered. The source goes through the reader in `lib/markdown`
 * and comes out as elements, never as HTML, so what an analyst types is text
 * on the page and nothing more. The front matter, when there is one, becomes
 * a fact sheet ahead of the body.
 */
export const Markdown = (props: MarkdownProps) => {
  const blocks = createMemo(() => parseMarkdown(props.source));

  return (
    <article class={styles.markdown}>
      <Blocks blocks={blocks()} />
    </article>
  );
};

const Blocks = (props: { blocks: readonly Block[] }) => (
  <For each={props.blocks}>{(block) => <BlockView block={block} />}</For>
);

const BlockView = (props: { block: Block }) => (
  <Switch>
    <Match when={props.block.kind === "frontMatter" ? props.block : undefined}>
      {(block) => (
        <dl class={styles.facts}>
          <For each={block().entries}>
            {(entry) => (
              <div class={styles.fact}>
                <dt>{entry.key.replaceAll("_", " ")}</dt>
                <dd>{entry.value}</dd>
              </div>
            )}
          </For>
        </dl>
      )}
    </Match>
    <Match when={props.block.kind === "heading" ? props.block : undefined}>
      {(block) => (
        <Dynamic component={`h${block().level}`} class={styles[`h${block().level}`]}>
          <Spans spans={block().children} />
        </Dynamic>
      )}
    </Match>
    <Match when={props.block.kind === "paragraph" ? props.block : undefined}>
      {(block) => (
        <p>
          <Spans spans={block().children} />
        </p>
      )}
    </Match>
    <Match when={props.block.kind === "list" ? props.block : undefined}>
      {(block) => (
        <Dynamic component={block().ordered ? "ol" : "ul"}>
          <For each={block().items}>
            {(item) => (
              <li>
                <Spans spans={item.children} />
                <Show when={item.nested}>{(nested) => <BlockView block={nested()} />}</Show>
              </li>
            )}
          </For>
        </Dynamic>
      )}
    </Match>
    <Match when={props.block.kind === "table" ? props.block : undefined}>
      {(block) => (
        <div class={styles.scroll}>
          <table class={styles.table}>
            <Show when={block().header}>
              {(header) => (
                <thead>
                  <tr>
                    <For each={header()}>
                      {(cell) => (
                        <th scope="col">
                          <Spans spans={cell} />
                        </th>
                      )}
                    </For>
                  </tr>
                </thead>
              )}
            </Show>
            <tbody>
              <For each={block().rows}>
                {(row) => (
                  <tr>
                    <For each={row}>
                      {(cell, index) => (
                        /* Without a header, the first column names the row like a label. */
                        <Dynamic
                          component={block().header === undefined && index() === 0 ? "th" : "td"}
                          scope={block().header === undefined && index() === 0 ? "row" : undefined}
                        >
                          <Spans spans={cell} />
                        </Dynamic>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      )}
    </Match>
    <Match when={props.block.kind === "quote" ? props.block : undefined}>
      {(block) => (
        <blockquote>
          <Blocks blocks={block().children} />
        </blockquote>
      )}
    </Match>
    <Match when={props.block.kind === "code" ? props.block : undefined}>
      {(block) => (
        <pre class={styles.code} data-language={block().language}>
          <code>{block().text}</code>
        </pre>
      )}
    </Match>
    <Match when={props.block.kind === "rule"}>
      <hr />
    </Match>
  </Switch>
);

const Spans = (props: { spans: readonly Inline[] }) => (
  <For each={props.spans}>{(span) => <SpanView span={span} />}</For>
);

/** Only http(s) and mail links are followed; anything else stays as words. */
const safeHref = (href: string): string | undefined =>
  /^(https?:|mailto:)/i.test(href) ? href : undefined;

const SpanView = (props: { span: Inline }) => (
  <Switch>
    <Match when={props.span.kind === "text" ? props.span : undefined}>
      {(span) => <>{span().text}</>}
    </Match>
    <Match when={props.span.kind === "strong" ? props.span : undefined}>
      {(span) => (
        <strong>
          <Spans spans={span().children} />
        </strong>
      )}
    </Match>
    <Match when={props.span.kind === "emphasis" ? props.span : undefined}>
      {(span) => (
        <em>
          <Spans spans={span().children} />
        </em>
      )}
    </Match>
    <Match when={props.span.kind === "code" ? props.span : undefined}>
      {(span) => <code class={styles.inlineCode}>{span().text}</code>}
    </Match>
    <Match when={props.span.kind === "link" ? props.span : undefined}>
      {(span) => (
        <Show
          when={safeHref(span().href)}
          fallback={<Spans spans={span().children} />}
        >
          {(href) => (
            <a href={href()} rel="noopener noreferrer" target="_blank">
              <Spans spans={span().children} />
            </a>
          )}
        </Show>
      )}
    </Match>
    <Match when={props.span.kind === "break"}>
      <br />
    </Match>
  </Switch>
);
